import { useCallback, useEffect, useState } from "react";
import { classNames, sendAuthenticatedRequest } from "../../common/functions";
import { Product } from "../../common/models/product";
import { complianceLabels, formatCo2e } from "../../common/models/compliance";
import { productInterestProps } from "../../visitorTracking";
import { rememberViewedProduct } from "../../buyerCare";
import {
  compareStandards,
  extractMaterialArray,
  extractStandardTokensFromText,
  formatStandardList,
  friendlyMaterialName,
  formatDimensions,
  getMaterialDisplayLabel,
  getProductDisplayTitle,
  getProductDisplayTitleInUnit,
  missingFlatSizeNote,
  StandardsComparison,
} from "./helpers";
import { convertPriceValueAndUnit } from "./price-helpers";
import { Price } from "./InventoryDetailsPrice";
import { AddToCartForm } from "./AddToCartForm";
import {
  useDimensionsUnit,
  useSearchHistory,
  useSearchResultsCache,
  useSuperUserMode,
} from "../../buyerStateDefs";
import { useUser } from "../../common/stateDefs";
import { KonoGuaranteeBadge } from "../KonoGuaranteeBadge";
import { AS9120Badge } from "../AS9120Badge";
import { useLocation, useNavigate } from "react-router-dom";
import { usePostHog } from "posthog-js/react";
import { captureAnalyticsEvent } from "../../analytics";
import { useSeoMeta } from "../../hooks/useSeoMeta";
import { ShieldMark } from "../SuperUser/ShieldMark";
import { SuperUserPricePanel } from "../SuperUser/SuperUserPricePanel";

type InventoryDetailsLocationState = {
  previousUrl?: string;
};

export const InventoryDetails = () => {
  const [product, setProduct] = useState<Product | null>(null);
  const [error, setError] = useState<string | null>(null);
  const dimensionUnit = useDimensionsUnit((state) => state.dimensionsUnit);
  const posthog = usePostHog();
  const superUserActive = useSuperUserMode((state) => state.active);
  const [superUserPanelOpen, setSuperUserPanelOpen] = useState(false);
  // Bumped after a super user saves an edit, to re-read the product so the page
  // shows the price a buyer would now see rather than the one it loaded with.
  const [reloadCount, setReloadCount] = useState(0);

  useEffect(() => {
    if (!product) {
      return;
    }
    captureAnalyticsEvent(posthog, "viewed_product_details", {
      source: "inventory_search",
      ...productInterestProps(product),
      productId: product?.id,
    });
    rememberViewedProduct(product);
  }, [product, posthog]);

  const searchResultsCache = useSearchResultsCache(
    (state) => state.searchResultsCache,
  );

  const targetPriceUnit = ["ft", "in"].includes(dimensionUnit) ? "lb" : "kg";

  const navigate = useNavigate();
  const location = useLocation();
  const previousUrl = (location.state as InventoryDetailsLocationState | null)
    ?.previousUrl;
  const handleBackClick = useCallback(() => {
    const currentOrigin = window.location.origin;
    const previousOrigin = getOrigin(previousUrl || document.referrer);
    const historyState = window.history.state as { idx?: number } | null;
    const hasHistoryEntry =
      typeof historyState?.idx === "number"
        ? historyState.idx > 0
        : window.history.length > 1;

    if (hasHistoryEntry && previousOrigin === currentOrigin) {
      navigate(-1);
      return;
    }

    window.location.assign(currentOrigin);
  }, [navigate, previousUrl]);

  useEffect(() => {
    const productId = new URLSearchParams(window.location.search).get("id");
    if (!productId) {
      return;
    }

    if (searchResultsCache) {
      for (const result of searchResultsCache.results) {
        if (result.id === productId) {
          setProduct(result);
          break;
        }
      }
    }

    sendAuthenticatedRequest(
      `${process.env.REACT_APP_BF_API_BASE_URL}/product/${productId}`,
      {},
    )
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Request failed (${response.status})`);
        }
        return response.json();
      })
      .then((data) => {
        setProduct(data);
      })
      .catch((err) => {
        console.error("Error fetching product details", err);
        setError("Error fetching product details");
      });
  }, [searchResultsCache, reloadCount]);

  const seoTitle = product
    ? `${getProductDisplayTitle(product)} | Kono`
    : undefined;
  const seoDescription = product
    ? `Buy ${getProductDisplayTitle(
        product,
      )} online at Kono — live inventory, ` +
      `instant pricing and RFQ upload. AS9120-certified, with DFARS-compliant ` +
      `material available.`
    : undefined;
  const seoJsonLd = product
    ? {
        "@context": "https://schema.org",
        "@type": "Product",
        name: getProductDisplayTitle(product),
        sku: product.id,
        ...(product.standard ? { additionalProperty: product.standard } : {}),
        offers: {
          "@type": "Offer",
          availability: "https://schema.org/InStock",
          ...(product.price?.value
            ? {
                price: product.price.value,
                priceCurrency: product.price.currency || "USD",
              }
            : {}),
          seller: {
            "@type": "Organization",
            name: "Kono",
            url: "https://kono.ai/",
          },
        },
      }
    : null;
  useSeoMeta({
    title: seoTitle,
    description: seoDescription,
    jsonLd: seoJsonLd,
  });

  if (error) {
    return (
      <>
        <BackButton onClick={handleBackClick} />
        <div className="mt-4 rounded-md bg-red-50 p-4 text-sm text-red-700">
          {error}
        </div>
      </>
    );
  }

  if (!product) {
    return <div>Loading...</div>;
  }

  const standardTokens = extractStandardTokens(product);
  const materialValue = normalizeFieldValue(
    removeStandardsFromMaterial(product, standardTokens) ||
      getMaterialDisplayLabel(product),
  );
  // Prefer an explicit per-item standard (e.g. High Temp Metals' full spec
  // package for this material+shape) when present; otherwise fall back to the
  // spec tokens auto-extracted from the summary/additionalSpecifications.
  // formatStandardList makes a space-mashed multi-spec package readable
  // ("ASTM B211 QQ A225/9 AMS 4123" -> "ASTM B211, QQ A225/9, AMS 4123").
  const standardValue = normalizeFieldValue(
    formatStandardList(product.standard) || standardTokens.join(", "),
  );
  // What the search asked for, so the page can say how this item compares.
  // Preferably the specs the server itself parsed; failing that the query in the
  // URL, which is what survives a reload or a link pasted to a colleague.
  const requestedStandards: string[] = Array.isArray(
    searchResultsCache?.structuredQuery?.requestedStandards,
  )
    ? searchResultsCache!.structuredQuery.requestedStandards
    : extractStandardTokensFromText(
        searchResultsCache?.searchQuery,
        new URLSearchParams(window.location.search).get("q") || undefined,
      );
  const standardsComparison = compareStandards(product, requestedStandards);
  const flatSizeNote = missingFlatSizeNote(product);
  const conditionValue = normalizeFieldValue(product.condition);
  const shapeValue = normalizeFieldValue(product.shape);
  const dimensionsValue = normalizeFieldValue(
    formatDimensions(product, dimensionUnit),
  );
  // On-hand quantity is deliberately not shown: we sell what we can source, not
  // only what is on the shelf, so buyers order the quantity they need and the
  // internal team is alerted about the lines that have to be procured.
  const priceDisplayUnits = ["kg", "lb", "ft", "in", "m", "mm", "unit"];
  const priceAvailable =
    !!product.price &&
    priceDisplayUnits.some((unit) => !!convertPriceValueAndUnit(product, unit));
  const leadTimeValue = formatLeadTime(product);
  const originComplianceValue = normalizeFieldValue(product.origin);
  // Stated compliance attributes join the origin line; a stated "no" is not
  // listed (buyers filter on what the vendor confirms, not on denials).
  const complianceValue = complianceLabels(product).join(", ");
  const co2eValue = formatCo2e(product);
  const additionalSpecificationsValue = normalizeFieldValue(
    product.additionalSpecifications,
  );

  return (
    <>
      <BackButton onClick={handleBackClick} />
      <h2 className="text-2xl font-bold tracking-tight text-gray-900 mt-2">
        {getProductDisplayTitleInUnit(product, dimensionUnit)}
      </h2>
      <div className="lg:grid lg:grid-cols-2 lg:gap-x-8 lg:gap-y-4 mt-6 md:mt-10">
        <div className="border-t border-gray-100">
          <dl className="divide-y divide-gray-100">
            <div className="px-4 py-6 sm:grid sm:grid-cols-2 sm:gap-4 sm:px-0">
              <dt className="text-sm font-medium leading-6 text-gray-900">
                Material
              </dt>
              <dd className="mt-1 text-sm leading-6 text-gray-700 sm:mt-0">
                {materialValue}
              </dd>
            </div>
            <div className="px-4 py-6 sm:grid sm:grid-cols-2 sm:gap-4 sm:px-0">
              <dt className="text-sm font-medium leading-6 text-gray-900">
                Standard
              </dt>
              <dd className="mt-1 text-sm leading-6 text-gray-700 sm:mt-0">
                {standardValue}
                {standardsComparison && (
                  <StandardsNote comparison={standardsComparison} />
                )}
              </dd>
            </div>
            <div className="px-4 py-6 sm:grid sm:grid-cols-2 sm:gap-4 sm:px-0">
              <dt className="text-sm font-medium leading-6 text-gray-900">
                Shape
              </dt>
              <dd className="mt-1 text-sm leading-6 text-gray-700 sm:mt-0">
                {shapeValue}
              </dd>
            </div>
            <div className="px-4 py-6 sm:grid sm:grid-cols-2 sm:gap-4 sm:px-0">
              <dt className="text-sm font-medium leading-6 text-gray-900">
                Dimensions
              </dt>
              <dd className="mt-1 text-sm leading-6 text-gray-700 sm:mt-0">
                {dimensionsValue}
                {flatSizeNote && (
                  <p className="mt-2 text-xs leading-5 text-gray-600">
                    {flatSizeNote}
                  </p>
                )}
              </dd>
            </div>
            <div className="px-4 py-6 sm:grid sm:grid-cols-2 sm:gap-4 sm:px-0">
              <dt className="text-sm font-medium leading-6 text-gray-900">
                Price
              </dt>
              <dd className="mt-1 flex items-center gap-2 text-sm leading-6 text-gray-700 sm:mt-0">
                {priceAvailable ? (
                  <Price product={product} targetPriceUnit={targetPriceUnit} />
                ) : (
                  ""
                )}
                {/* Shown only while a Kono super user has the tools unlocked;
                    it opens the price provenance and item editor. */}
                {superUserActive && (
                  <button
                    type="button"
                    onClick={() => setSuperUserPanelOpen(true)}
                    className="text-[#00062e] hover:opacity-70"
                    title="Where this price came from (Kono super user)"
                    aria-label="Show price detail for Kono super users"
                  >
                    <ShieldMark className="h-4 w-4" />
                  </button>
                )}
              </dd>
            </div>
            <div className="px-4 py-6 sm:grid sm:grid-cols-2 sm:gap-4 sm:px-0">
              <dt className="text-sm font-medium leading-6 text-gray-900">
                Lead Time
              </dt>
              <dd className="mt-1 text-sm leading-6 text-gray-700 sm:mt-0">
                {leadTimeValue}
              </dd>
            </div>
            <div className="px-4 py-6 sm:grid sm:grid-cols-2 sm:gap-4 sm:px-0">
              <dt className="text-sm font-medium leading-6 text-gray-900">
                Condition
              </dt>
              <dd className="mt-1 text-sm leading-6 text-gray-700 sm:mt-0">
                {conditionValue}
              </dd>
            </div>
            <div className="px-4 py-6 sm:grid sm:grid-cols-2 sm:gap-4 sm:px-0">
              <dt className="text-sm font-medium leading-6 text-gray-900">
                Origin / Compliance
              </dt>
              <dd className="mt-1 text-sm leading-6 text-gray-700 sm:mt-0">
                {originComplianceValue}
                {complianceValue && (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {complianceLabels(product).map((label) => (
                      <span
                        key={label}
                        className="inline-flex items-center rounded-md bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700 ring-1 ring-inset ring-emerald-600/20"
                      >
                        {label}
                      </span>
                    ))}
                  </div>
                )}
              </dd>
            </div>
            {co2eValue && (
              <div className="px-4 py-6 sm:grid sm:grid-cols-2 sm:gap-4 sm:px-0">
                <dt className="text-sm font-medium leading-6 text-gray-900">
                  Carbon footprint
                </dt>
                <dd className="mt-1 text-sm leading-6 text-gray-700 sm:mt-0">
                  {co2eValue}
                </dd>
              </div>
            )}
            <div className="px-4 py-6 sm:grid sm:grid-cols-2 sm:gap-4 sm:px-0">
              <dt className="text-sm font-medium leading-6 text-gray-900">
                Additional Specifications
              </dt>
              <dd className="mt-1 text-sm leading-6 text-gray-700 sm:mt-0">
                {additionalSpecificationsValue}
              </dd>
            </div>
          </dl>
          <div className="mt-4">
            <InquireForm product={product} />
          </div>
        </div>
        <div>
          <AddToCartForm product={product} />
          <div className="mt-2 mb-5 flex flex-wrap items-center gap-4">
            <KonoGuaranteeBadge />
            <AS9120Badge imgClassName="h-8" />
            <OnHandMark
              onHand={product.onHand}
              priceCheck={product.priceCheck}
            />
          </div>
          <SaveProductButton product={product} />
        </div>
        <div></div>
      </div>
      {superUserActive && superUserPanelOpen && (
        <SuperUserPricePanel
          productId={product.id}
          onClose={() => setSuperUserPanelOpen(false)}
          onSaved={() => setReloadCount((count) => count + 1)}
        />
      )}
    </>
  );
};

/**
 * The small "OH" / "PR" marks on the far right of the badges row under the
 * Add to Cart button. "OH"
 * (on hand) is a freshness signal, colored by how recently any of our sources
 * last confirmed this item; its thresholds live in the internal General
 * Configurations screen. "PR" (price) shows whether the displayed price came
 * directly from the source (green — uploaded, scraped, or parsed from
 * procurement, even if we adjusted it) or was inferred (red — comparable
 * products/sizes, density table, or LLM web search). The API sends both
 * buckets already computed; each mark renders only when its bucket is known.
 *
 * These are internal quality signals, so they only render for team members —
 * anyone signed in with a @kono.ai account — never for buyers.
 */
const OnHandMark = ({
  onHand,
  priceCheck,
}: {
  onHand?: Product["onHand"];
  priceCheck?: Product["priceCheck"];
}) => {
  const user = useUser((state) => state.user);
  const isKonoTeamMember = (user?.email || "")
    .trim()
    .toLowerCase()
    .endsWith("@kono.ai");
  if (!isKonoTeamMember) return null;
  if (!onHand && !priceCheck) return null;
  const dotClass = (level: "green" | "yellow" | "red") =>
    level === "green"
      ? "bg-green-500"
      : level === "yellow"
        ? "bg-yellow-400"
        : "bg-red-500";
  return (
    <div className="ml-auto">
      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide text-gray-400">
        {onHand && (
          <span
            className="inline-flex items-center gap-1"
            title={`On hand — availability last confirmed ${
              onHand.ageDays === 0
                ? "today"
                : onHand.ageDays === 1
                  ? "1 day ago"
                  : `${onHand.ageDays} days ago`
            }`}
          >
            <span
              className={`inline-block h-2 w-2 rounded-full ${dotClass(onHand.level)}`}
            />
            OH
          </span>
        )}
        {onHand && priceCheck && <span>-</span>}
        {priceCheck && (
          <span
            className="inline-flex items-center gap-1"
            title={
              priceCheck.level === "green"
                ? "Price — validated directly from the source"
                : "Price — inferred from comparable inventory or market data"
            }
          >
            <span
              className={`inline-block h-2 w-2 rounded-full ${dotClass(priceCheck.level)}`}
            />
            PR
          </span>
        )}
      </span>
    </div>
  );
};

const extractStandardTokens = (product: Product): string[] => {
  return extractStandardTokensFromText(
    product.summary,
    product.additionalSpecifications,
    product.condition,
    ...extractMaterialArray(product),
  );
};

const removeStandardsFromMaterial = (
  product: Product,
  standards: string[],
): string => {
  const standardSet = new Set(standards.map((token) => token.toLowerCase()));
  return extractMaterialArray(product)
    .filter((value) => !standardSet.has(value.toLowerCase()))
    .map(friendlyMaterialName)
    .join(", ");
};

/**
 * Renders the handling window we quote before an item ships ("Min 3 and Max 5
 * days + shipping time"). Carrier transit is added on top at checkout, hence the
 * explicit "+ shipping time" rather than a single delivery figure. Items with no
 * stored range render blank, like every other unknown field on this page.
 */
const formatLeadTime = (product: Product): string => {
  const min = product.estimatedLeadTimeDaysMin;
  const max = product.estimatedLeadTimeDaysMax;
  if (typeof min !== "number" || typeof max !== "number") {
    return "";
  }
  return `Min ${min} and Max ${max} days + shipping time`;
};

const normalizeFieldValue = (value: string | null | undefined): string => {
  const normalized = (value || "").trim();
  if (normalized === "-") {
    return "";
  }
  return normalized.length > 0 ? normalized : "";
};

const getOrigin = (url: string) => {
  if (!url) {
    return null;
  }

  try {
    return new URL(url).origin;
  } catch {
    return null;
  }
};

const BackButton = ({ onClick }: { onClick: () => void }) => (
  <button
    type="button"
    onClick={onClick}
    className="text-sm text-gray-500 hover:text-gray-700 cursor-pointer"
  >
    &larr; Back
  </button>
);

const StandardsNote = ({ comparison }: { comparison: StandardsComparison }) => {
  const { matched, missing } = comparison;
  if (missing.length === 0) {
    return (
      <p className="mt-2 text-xs leading-5 text-green-700">
        Certified to {matched.join(" and ")}, as your search asked for.
      </p>
    );
  }
  return (
    <p className="mt-2 text-xs leading-5 text-amber-700">
      Your search asked for {missing.join(" and ")}, which this item's paperwork
      does not name
      {matched.length > 0
        ? `, though it does carry ${matched.join(" and ")}`
        : ""}
      . We show it because it is the same alloy and may still suit you — ask us
      and we will confirm the certification, or tick "Only items certified to{" "}
      {comparison.requested.join(" and ")}" in the Standard filter to leave
      these out.
    </p>
  );
};

const SaveProductButton = ({ product }: { product: Product }) => {
  const [savedProducts, setSavedProducts] = useState<Product[]>([]);

  useEffect(() => {
    const saved = localStorage.getItem("savedProducts");
    if (saved) {
      setSavedProducts(JSON.parse(saved));
    }
  }, [localStorage.getItem("savedProducts")]);

  const productSaved = savedProducts.some((p) => p.id === product.id);

  const toggleCart = () => {
    if (savedProducts.some((p) => p.id === product.id)) {
      const newCartItems = savedProducts.filter((p) => p.id !== product.id);
      setSavedProducts(newCartItems);
      localStorage.setItem("savedProducts", JSON.stringify(newCartItems));
    } else {
      const newCartItems = [...savedProducts, product];
      setSavedProducts(newCartItems);
      localStorage.setItem("savedProducts", JSON.stringify(newCartItems));
    }
  };
  return (
    <button
      onClick={() => toggleCart()}
      className="rounded-full bg-white border-2 border-[#00062e] hover:bg-slate-100 focus:bg-slate-100 text-[#00062e] font-bold block w-full mt-3 py-2"
    >
      {productSaved ? "Remove from bookmarked" : "Bookmark product"}
    </button>
  );
};

type InquireFormProps = {
  product: Product;
};

const InquireForm = ({ product }: InquireFormProps) => {
  const user = useUser((state) => state.user);
  const [open, setOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submissionError, setSubmissionError] = useState(false);
  const searchQueryHistory = useSearchHistory(
    (state) => state.sessionSearchQueryHistory,
  );

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitted(false);
    setSubmissionError(false);
    const formData = new FormData(event.currentTarget);
    const data = Object.fromEntries(formData.entries());
    await sendAuthenticatedRequest(
      `${process.env.REACT_APP_BF_API_BASE_URL}/inquire`,
      {
        method: "POST",
        jsonBody: {
          ...data,
          product,
          searchQueryHistory,
        },
      },
    )
      .then((response) => {
        setSubmitted(true);
      })
      .catch((err) => setSubmissionError(true));
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="rounded-full bg-white border-2 border-[#00062e] hover:bg-slate-100 focus:bg-slate-100 text-[#00062e] font-bold block w-full mt-3 py-2"
      >
        Request Additional Information
      </button>
    );
  }

  return (
    <div>
      <div className="flex items-center mt-3 mb-3">
        <div className="flex-grow border-t border-gray-300 mt-2"></div>
        <span
          className="px-4 font-bold text-gray-900 pt-2 cursor-pointer"
          onClick={() => setOpen(false)}
        >
          Request Additional Information
        </span>
        <div className="flex-grow border-t border-gray-300 mt-2"></div>
      </div>
      <div className="animate-expand-down overflow-hidden">
        <form onSubmit={handleSubmit}>
          <div className="isolate -space-y-px rounded-xl shadow-sm mt-3">
            <div
              className={classNames(
                "relative rounded-xl rounded-b-none px-3 pb-1.5 pt-2.5 ring-1 ring-inset ring-gray-300 focus-within:z-10 focus-within:ring-2 focus-within:ring-[#00062e]",
                user && "hidden",
              )}
            >
              <label
                htmlFor="input-name"
                className="block text-xs font-medium text-gray-900"
              >
                Name
              </label>
              <input
                id="input-name"
                name="name"
                type="text"
                placeholder="Jane Smith"
                value={user?.displayName}
                className="block w-full border-0 p-0 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6"
              />
            </div>
            <div
              className={classNames(
                "relative px-3 pb-1.5 pt-2.5 ring-1 ring-inset ring-gray-300 focus-within:z-10 focus-within:ring-2 focus-within:ring-[#00062e]",
                user && "hidden",
              )}
            >
              <label
                htmlFor="input-email"
                className="block text-xs font-medium text-gray-900"
              >
                Email
              </label>
              <input
                id="input-email"
                name="email"
                type="email"
                placeholder="name@yourcompany.com"
                value={user?.email}
                className="block w-full border-0 p-0 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6"
              />
            </div>
            <div
              className={classNames(
                "relative rounded-xl px-3 pb-1.5 pt-2.5 ring-1 ring-inset ring-gray-300 focus-within:z-10 focus-within:ring-2 focus-within:ring-[#00062e]",
                !user && "rounded-t-none",
              )}
            >
              <label
                htmlFor="input-message"
                className="block text-xs font-medium text-gray-900"
              >
                Message
              </label>
              <input
                id="input-message"
                name="message"
                type="text"
                placeholder="..."
                className="block w-full border-0 p-0 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6"
              />
            </div>
          </div>
          <div className="text-xs text-gray-500 mt-2">
            The product details will be included in your inquiry, and you will
            receive a confirmation email upon submission.
          </div>
          <button
            type="submit"
            className="rounded-full bg-[#00062e] hover:bg-slate-900 focus:bg-slate-900 text-white font-bold block w-full mt-3 py-2"
          >
            Send
          </button>
          {submitted && <div>Thanks! You will hear from us soon.</div>}
          {submissionError && (
            <div>Error sending your communication, please try again.</div>
          )}
        </form>
      </div>
    </div>
  );
};
